/**
 * React hook for PostHog feature flags
 */

import { useState, useEffect } from 'react';
import { usePosthog } from './usePosthog';
import { featureFlagManager } from '~/services/posthog';

/**
 * Hook to check if a feature flag is enabled
 * Uses global feature flag manager to prevent memory leaks
 */
export const useFeatureFlag = (flagKey: string, defaultValue: boolean = false): boolean => {
  const posthogService = usePosthog();
  const [isEnabled, setIsEnabled] = useState<boolean>(defaultValue);

  useEffect(() => {
    // Initialize the manager with PostHog service
    featureFlagManager.initialize(posthogService);

    // Get initial flag value
    const initialValue = featureFlagManager.getFeatureFlag(flagKey, defaultValue);
    setIsEnabled(initialValue);

    // Subscribe to flag updates
    const unsubscribe = featureFlagManager.subscribe(flagKey, (enabled) => {
      setIsEnabled(enabled);
    });

    // Reload flags to ensure we have the latest
    featureFlagManager.reloadFeatureFlags();

    // Cleanup subscription on unmount or dependency change
    return () => {
      unsubscribe();
    };
  }, [flagKey, defaultValue, posthogService]);

  return isEnabled;
};

/**
 * Hook to get feature flag with payload
 * Uses global feature flag manager to prevent memory leaks
 */
export const useFeatureFlagWithPayload = <T = any>(
  flagKey: string,
  defaultValue: boolean = false
): { enabled: boolean; payload: T | null } => {
  const posthogService = usePosthog();
  const [state, setState] = useState<{ enabled: boolean; payload: T | null }>({
    enabled: defaultValue,
    payload: null,
  });

  useEffect(() => {
    // Initialize the manager with PostHog service
    featureFlagManager.initialize(posthogService);

    // Get initial flag value and payload
    const initialEnabled = featureFlagManager.getFeatureFlag(flagKey, defaultValue);
    const initialPayload = featureFlagManager.getFeatureFlagPayload(flagKey);
    setState({ enabled: initialEnabled, payload: initialPayload });

    // Subscribe to flag updates with payload
    const unsubscribe = featureFlagManager.subscribe(flagKey, (enabled, payload) => {
      setState({ enabled, payload: payload || null });
    });

    // Reload flags to ensure we have the latest
    featureFlagManager.reloadFeatureFlags();

    // Cleanup subscription on unmount or dependency change
    return () => {
      unsubscribe();
    };
  }, [flagKey, defaultValue, posthogService]);

  return state;
};